import type { SavedViewRule, SavedViewSort, SmartList } from '@portfolio-pm/shared';
import { DEFAULT_RULE_TREE, ruleTreeToQuery } from '@portfolio-pm/smart-list';
import { type FormEvent, useState } from 'react';

/**
 * Saved-view (smart list) creation/edit form with the visual compound
 * rule builder and sort/group editor (REQ-SMART-005). Extracted from
 * Sidebar.tsx.
 */

const SAVED_VIEW_GROUP_OPTIONS: Array<{ value: SmartList['groupBy']; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'project', label: 'Project' },
  { value: 'tag', label: 'Tag' },
  { value: 'priority', label: 'Priority' },
  { value: 'due', label: 'Due date' },
  { value: 'scheduled', label: 'Scheduled date' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'owner', label: 'Owner' },
];

const SAVED_VIEW_SORT_OPTIONS: Array<{ value: SavedViewSort['key']; label: string }> = [
  { value: 'name', label: 'Task name' },
  { value: 'priority', label: 'Priority' },
  { value: 'due', label: 'Due date' },
  { value: 'scheduled', label: 'Scheduled date' },
  { value: 'tag', label: 'Tag' },
  { value: 'project', label: 'Project' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
];

export interface SmartListFormValue {
  name: string;
  query: string;
  ruleTree?: SavedViewRule;
  groupBy?: SmartList['groupBy'];
  groupDirection?: SmartList['groupDirection'];
  sort?: SavedViewSort[];
  showCompleted?: boolean;
  showTrashed?: boolean;
}

export function SmartListForm({
  initialName = '',
  initialQuery = 'incomplete',
  initialRuleTree,
  initialGroupBy = 'project',
  initialGroupDirection = 'asc',
  initialSort = [{ key: 'due', direction: 'asc' }],
  initialShowCompleted = true,
  initialShowTrashed = false,
  onCommit,
  onCancel,
}: {
  initialName?: string;
  initialQuery?: string;
  initialRuleTree?: SavedViewRule | undefined;
  initialGroupBy?: SmartList['groupBy'];
  initialGroupDirection?: SmartList['groupDirection'];
  initialSort?: SavedViewSort[];
  initialShowCompleted?: boolean;
  initialShowTrashed?: boolean;
  onCommit(value: SmartListFormValue): void;
  onCancel(): void;
}): JSX.Element {
  const [name, setName] = useState(initialName);
  const [ruleTree, setRuleTree] = useState<SavedViewRule>(
    initialRuleTree ?? { type: 'condition', field: 'raw', operator: 'is', value: initialQuery },
  );
  const [groupBy, setGroupBy] = useState<SmartList['groupBy']>(initialGroupBy);
  const [groupDirection, setGroupDirection] =
    useState<SmartList['groupDirection']>(initialGroupDirection);
  const [sort, setSort] = useState<SavedViewSort[]>(initialSort);
  const [showCompleted, setShowCompleted] = useState(initialShowCompleted);
  const [showTrashed, setShowTrashed] = useState(initialShowTrashed);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const query = ruleTreeToQuery(ruleTree).trim() || 'incomplete';
    onCommit({
      name,
      query,
      ruleTree,
      groupBy,
      groupDirection,
      sort,
      showCompleted,
      showTrashed,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <input
        // biome-ignore lint/a11y/noAutofocus: this input is conditionally mounted on user action
        autoFocus
        data-testid="smart-list-name-input"
        value={name}
        placeholder="Saved view name"
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
        }}
        className="ui-input"
      />
      <RuleBuilder rule={ruleTree} onChange={setRuleTree} />
      <input
        data-testid="smart-list-query-input"
        value={ruleTreeToQuery(ruleTree)}
        placeholder="priority:1 AND incomplete"
        onChange={(event) =>
          setRuleTree({
            type: 'condition',
            field: 'raw',
            operator: 'is',
            value: event.target.value,
          })
        }
        className="ui-input"
      />
      <details className="rounded-md bg-[rgba(29,37,36,0.05)] p-2" open>
        <summary className="cursor-pointer text-xs font-semibold text-[color:var(--app-text)]">
          Advanced sort and grouping
        </summary>
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-1">
              <span className="ui-label">Group by</span>
              <select
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as SmartList['groupBy'])}
                className="ui-select"
              >
                {SAVED_VIEW_GROUP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="ui-label">Group sort</span>
              <select
                value={groupDirection}
                onChange={(event) =>
                  setGroupDirection(event.target.value as SmartList['groupDirection'])
                }
                className="ui-select"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </label>
          </div>
          <SortCriteriaEditor sort={sort} onChange={setSort} />
        </div>
      </details>
      <label className="flex items-center gap-2 text-xs text-[color:var(--app-text-muted)]">
        <input
          type="checkbox"
          checked={showCompleted}
          onChange={(event) => setShowCompleted(event.target.checked)}
        />
        Show completed
      </label>
      <label className="flex items-center gap-2 text-xs text-[color:var(--app-text-muted)]">
        <input
          type="checkbox"
          checked={showTrashed}
          onChange={(event) => setShowTrashed(event.target.checked)}
        />
        Show trash
      </label>
      <div className="flex gap-2">
        <button type="submit" className="ui-button ui-button-primary h-8 px-2 text-xs">
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="ui-button ui-button-subtle h-8 px-2 text-xs"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function SortCriteriaEditor({
  sort,
  onChange,
}: {
  sort: SavedViewSort[];
  onChange(sort: SavedViewSort[]): void;
}): JSX.Element {
  const actualSort = sort.length > 0 ? sort : [{ key: 'due', direction: 'asc' } as SavedViewSort];

  const updateCriterion = (index: number, patch: Partial<SavedViewSort>): void => {
    onChange(
      actualSort.map((criterion, i) => (i === index ? { ...criterion, ...patch } : criterion)),
    );
  };

  const moveCriterion = (index: number, delta: -1 | 1): void => {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= actualSort.length) return;
    const next = [...actualSort];
    const [item] = next.splice(index, 1);
    if (!item) return;
    next.splice(nextIndex, 0, item);
    onChange(next);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="ui-label">Task sort criteria</span>
        <button
          type="button"
          className="ui-button ui-button-subtle h-7 px-2 text-xs"
          onClick={() => onChange([...actualSort, { key: 'due', direction: 'asc' }])}
        >
          Add
        </button>
      </div>
      {actualSort.map((criterion, index) => (
        <div
          key={`${criterion.key}-${index}`}
          className="grid grid-cols-[1fr_6.75rem_auto_auto_auto] items-center gap-1"
        >
          <select
            value={criterion.key}
            onChange={(event) =>
              updateCriterion(index, { key: event.target.value as SavedViewSort['key'] })
            }
            className="ui-select py-1 text-xs"
          >
            {SAVED_VIEW_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={criterion.direction}
            onChange={(event) =>
              updateCriterion(index, {
                direction: event.target.value as SavedViewSort['direction'],
              })
            }
            className="ui-select py-1 text-xs"
          >
            <option value="asc">Asc</option>
            <option value="desc">Desc</option>
          </select>
          <button
            type="button"
            aria-label="Move sort criterion up"
            disabled={index === 0}
            onClick={() => moveCriterion(index, -1)}
            className="ui-button ui-button-subtle h-8 w-8 px-0 text-xs"
          >
            Up
          </button>
          <button
            type="button"
            aria-label="Move sort criterion down"
            disabled={index === actualSort.length - 1}
            onClick={() => moveCriterion(index, 1)}
            className="ui-button ui-button-subtle h-8 w-8 px-0 text-xs"
          >
            Down
          </button>
          <button
            type="button"
            aria-label="Remove sort criterion"
            disabled={actualSort.length === 1}
            onClick={() => onChange(actualSort.filter((_, i) => i !== index))}
            className="ui-button ui-button-subtle h-8 w-8 px-0 text-xs"
          >
            -
          </button>
        </div>
      ))}
    </div>
  );
}

function RuleBuilder({
  rule,
  onChange,
  depth = 0,
}: {
  rule: SavedViewRule;
  onChange(rule: SavedViewRule): void;
  depth?: number;
}): JSX.Element {
  if (rule.type === 'condition') {
    return (
      <div className="rounded-lg border border-[color:var(--app-border)] bg-white/65 p-2">
        <div className="grid grid-cols-[1fr_1fr_1.4fr] gap-1">
          <select
            value={rule.field}
            onChange={(event) =>
              onChange({
                ...rule,
                field: event.target.value as Extract<SavedViewRule, { type: 'condition' }>['field'],
              })
            }
            className="ui-select py-1 text-xs"
          >
            {[
              'raw',
              'text',
              'title',
              'completed',
              'trashed',
              'priority',
              'due',
              'scheduled',
              'tag',
              'project',
              'workflow',
              'estimate',
              'recurrence',
              'owner',
              'url',
              'location',
              'energy',
              'daypart',
              'child',
              'root',
              'haschildren',
            ].map((field) => (
              <option key={field} value={field}>
                {field}
              </option>
            ))}
          </select>
          <select
            value={rule.operator}
            onChange={(event) =>
              onChange({
                ...rule,
                operator: event.target.value as Extract<
                  SavedViewRule,
                  { type: 'condition' }
                >['operator'],
              })
            }
            className="ui-select py-1 text-xs"
          >
            {['is', 'is_not', 'contains', 'exists', 'missing', 'before', 'after', 'on'].map(
              (operator) => (
                <option key={operator} value={operator}>
                  {operator}
                </option>
              ),
            )}
          </select>
          <input
            value={rule.value ?? ''}
            onChange={(event) => onChange({ ...rule, value: event.target.value })}
            placeholder={rule.field === 'raw' ? 'priority:1 AND tag:work' : 'value'}
            className="ui-input py-1 text-xs"
          />
        </div>
      </div>
    );
  }

  const updateChild = (index: number, child: SavedViewRule): void => {
    onChange({ ...rule, children: rule.children.map((item, i) => (i === index ? child : item)) });
  };

  return (
    <div
      className="space-y-2 rounded-lg border border-[color:var(--app-border)] bg-white/55 p-2"
      style={{ marginLeft: depth > 0 ? 8 : 0 }}
    >
      <div className="flex items-center gap-2">
        <select
          value={rule.operator}
          onChange={(event) =>
            onChange({
              ...rule,
              operator: event.target.value as Extract<SavedViewRule, { type: 'group' }>['operator'],
            })
          }
          className="ui-select h-8 py-1 text-xs"
        >
          <option value="all">All</option>
          <option value="any">Any</option>
          <option value="none">None</option>
        </select>
        <button
          type="button"
          className="ui-button ui-button-subtle h-8 px-2 text-xs"
          onClick={() =>
            onChange({
              ...rule,
              children: [...rule.children, { type: 'condition', field: 'tag', operator: 'is' }],
            })
          }
        >
          + Condition
        </button>
        <button
          type="button"
          className="ui-button ui-button-subtle h-8 px-2 text-xs"
          onClick={() =>
            onChange({
              ...rule,
              children: [...rule.children, { ...DEFAULT_RULE_TREE }],
            })
          }
        >
          + Group
        </button>
      </div>
      {rule.children.map((child, index) => (
        <div key={`${child.type}-${index}`} className="flex gap-1">
          <div className="min-w-0 flex-1">
            <RuleBuilder
              rule={child}
              onChange={(next) => updateChild(index, next)}
              depth={depth + 1}
            />
          </div>
          <button
            type="button"
            aria-label="Delete rule"
            onClick={() =>
              onChange({ ...rule, children: rule.children.filter((_, i) => i !== index) })
            }
            className="ui-button ui-button-subtle h-8 w-8 px-0 text-xs"
          >
            -
          </button>
        </div>
      ))}
    </div>
  );
}
